import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { listRecipes } from '../../api/recipes.js';
import type { Ingredient, MealType, Recipe } from '../../api/types.js';
import { listIngredients } from '../../api/ingredients.js';
import { readRecipeCache, writeRecipeCache } from '../../api/recipeCache.js';
import { filterRecipes } from '../../api/recipeFilter.js';
import { useToast } from '../../hooks/useToast.js';
import { RecipeCard } from './RecipeCard.js';

function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return err instanceof TypeError;
}

interface RecipeCatalogProps {
  fridgeIds: number[];
  onRemoveFridge: (id: number) => void;
  onClearFridge: () => void;
  onOpenFridge: () => void;
  onOpenNew: () => void;
  onOpenScraper: () => void;
  onOpenRecipe: (id: number) => void;
  refreshKey: number;
}

const MEAL_FILTERS: ReadonlyArray<{ value: '' | MealType; label: string }> = [
  { value: '', label: 'All' },
  { value: 'breakfast', label: '🌅 Breakfast' },
  { value: 'lunch', label: '☀️ Lunch' },
  { value: 'dinner', label: '🌙 Dinner' },
  { value: 'snack', label: '🍿 Snack' },
];

export function RecipeCatalog({
  fridgeIds,
  onRemoveFridge,
  onClearFridge,
  onOpenFridge,
  onOpenNew,
  onOpenScraper,
  onOpenRecipe,
  refreshKey,
}: RecipeCatalogProps): ReactElement {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mealFilter, setMealFilter] = useState<'' | MealType>('');
  const [cachedAll, setCachedAll] = useState<Recipe[]>(() => readRecipeCache() ?? []);
  const [recipes, setRecipes] = useState<Recipe[]>(cachedAll);
  const [offline, setOffline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine === false : false,
  );
  const [fridgeMap, setFridgeMap] = useState<Map<number, Ingredient>>(new Map());
  const debounceRef = useRef<number | null>(null);
  const cachedAllRef = useRef<Recipe[]>(cachedAll);

  useEffect(() => {
    const onOnline = (): void => setOffline(false);
    const onOffline = (): void => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    setCachedAll(readRecipeCache() ?? []);
  }, [refreshKey]);

  useEffect(() => {
    cachedAllRef.current = cachedAll;
  }, [cachedAll]);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    const clientFilter = (): void => {
      const filtered = filterRecipes(cachedAllRef.current, {
        search: debouncedSearch || undefined,
        meal_type: mealFilter || undefined,
        fridge_ingredients: fridgeIds,
      });
      if (!cancelled) setRecipes(filtered);
    };

    if (offline) {
      clientFilter();
      return () => {
        cancelled = true;
      };
    }

    listRecipes({
      search: debouncedSearch || undefined,
      meal_type: mealFilter || undefined,
      fridge_ingredients: fridgeIds,
    })
      .then((rows) => {
        if (cancelled) return;
        setRecipes(rows);
        if (debouncedSearch === '' && mealFilter === '' && fridgeIds.length === 0) {
          writeRecipeCache(rows);
          setCachedAll(rows);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNetworkError(err)) {
          clientFilter();
          return;
        }
        const msg = err instanceof Error ? err.message : 'Failed to load';
        toast.push('error', msg);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, mealFilter, fridgeIds, refreshKey, offline, toast]);

  useEffect(() => {
    if (fridgeIds.length === 0) {
      setFridgeMap(new Map());
      return;
    }
    let cancelled = false;
    listIngredients({}).then((rows) => {
      if (cancelled) return;
      const next = new Map<number, Ingredient>();
      for (const r of rows) {
        if (fridgeIds.includes(r.id)) next.set(r.id, r);
      }
      setFridgeMap(next);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [fridgeIds]);

  const fridgeChips = useMemo(
    () => fridgeIds.map((id) => fridgeMap.get(id)).filter((x): x is Ingredient => x !== undefined),
    [fridgeIds, fridgeMap],
  );

  return (
    <div className="flex flex-col gap-3 p-4" data-testid="recipe-catalog">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">📖 Recipes</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenScraper}
            className="rounded-md border px-3 py-2 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 40,
            }}
            data-testid="recipes-scraper"
            aria-label="Import from URL"
          >
            🌐
          </button>
          <button
            type="button"
            onClick={onOpenNew}
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              borderColor: 'var(--accent)',
              color: 'var(--bg-primary, #fff)',
              minHeight: 40,
            }}
            data-testid="recipes-new"
          >
            + New
          </button>
        </div>
      </header>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Search recipes or ingredients..."
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={{
          background: 'var(--bg-primary, transparent)',
          borderColor: 'var(--border)',
          color: 'var(--text-main)',
        }}
        data-testid="recipes-search"
      />

      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Meal type filter">
        {MEAL_FILTERS.map((f) => {
          const active = mealFilter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={active}
              data-active={active ? 'true' : 'false'}
              onClick={() => setMealFilter(f.value)}
              className="rounded-full border px-3 py-1 text-xs"
              style={{
                borderColor: active ? 'var(--accent)' : 'var(--border)',
                color: active ? 'var(--accent)' : 'var(--text-main)',
                fontWeight: active ? 600 : 400,
                background: active
                  ? 'color-mix(in srgb, var(--accent) 15%, transparent)'
                  : 'transparent',
              }}
              data-testid={`meal-pill-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onOpenFridge}
          className="ml-auto rounded-full border px-3 py-1 text-xs"
          style={{
            borderColor: fridgeIds.length > 0 ? 'var(--accent)' : 'var(--border)',
            color: fridgeIds.length > 0 ? 'var(--accent)' : 'var(--text-main)',
          }}
          data-testid="recipes-fridge-toggle"
        >
          🥕 My Fridge
        </button>
      </div>

      {fridgeIds.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-1 rounded-md border p-2"
          style={{ borderColor: 'var(--border)' }}
          data-testid="recipes-fridge-chips"
        >
          {fridgeChips.map((i) => (
            <span
              key={i.id}
              className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-main)',
                background: 'var(--bg-surface)',
              }}
            >
              {i.name}
              <button
                type="button"
                aria-label={`Remove ${i.name}`}
                onClick={() => onRemoveFridge(i.id)}
                className="flex h-5 w-5 items-center justify-center"
                style={{ color: 'var(--text-muted)' }}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearFridge}
            className="ml-1 text-xs underline"
            style={{ color: 'var(--accent)' }}
            data-testid="recipes-fridge-clear"
          >
            Clear
          </button>
        </div>
      ) : null}

      {offline && recipes.length > 0 ? (
        <div
          className="rounded-md border p-2 text-xs"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-muted)',
            background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
          }}
          data-testid="recipes-offline-banner"
        >
          📴 Offline — showing cached recipes
        </div>
      ) : null}

      <div className="flex flex-col gap-2" data-testid="recipes-list">
        {recipes.length === 0 ? (
          <div
            className="rounded-xl border p-6 text-center text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
            }}
            data-testid="recipes-empty"
          >
            {fridgeIds.length > 0
              ? 'None of your fridge items appear in any recipe.'
              : debouncedSearch.length > 0
              ? 'No recipes match your search.'
              : mealFilter
              ? `No ${mealFilter} recipes yet.`
              : 'No recipes yet. Tap + New to create one.'}
          </div>
        ) : (
          recipes.map((r) => (
            <RecipeCard key={r.id} recipe={r} onClick={() => onOpenRecipe(r.id)} />
          ))
        )}
      </div>
    </div>
  );
}